import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useState } from 'react'

import LinedTextArea from '../../components/tools/LinedTextArea'
import ToolWorkspace from '../../components/tools/ToolWorkspace'
import { type ToolAbout, ToolIoChip, WrapLinesPanelSwitch } from '../../components/tools/toolChrome'
import { getCodec, JSON_TOOL_CODECS, MAX_CODEC_INPUT_CHARS, type ToolMode } from '../../lib/codecs'

const JSON_CODEC_ID = JSON_TOOL_CODECS[0].id

const JSON_ABOUT: ToolAbout = {
  description:
    'Make JSON easier to read, or minify it to one line. The browser parses your input and writes it again. Invalid JSON returns an error. There is no schema check and no linter, only structure.',
  libraries: [
    { name: 'MDN: JSON.parse and JSON.stringify', href: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON' },
  ],
}

export default function JsonTool() {
  const codec = useMemo(() => getCodec(JSON_CODEC_ID)!, [])
  const [mode, setMode] = useState<ToolMode>('encode')
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lineWrap, setLineWrap] = useState(false)

  const run = useCallback(async () => {
    try {
      setError(null)
      if (input.length > MAX_CODEC_INPUT_CHARS) {
        setError(`Input exceeds ${MAX_CODEC_INPUT_CHARS.toLocaleString()} characters (browser safety limit).`)
        return
      }
      setBusy(true)
      const out =
        mode === 'encode'
          ? await Promise.resolve(codec.encode(input))
          : await Promise.resolve(codec.decode(input))
      setOutput(out)
    } catch (e) {
      setError(e instanceof Error ? e.message : mode === 'encode' ? 'Prettify failed' : 'Minify failed')
    } finally {
      setBusy(false)
    }
  }, [input, mode, codec])

  const copy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      setError('Could not copy to clipboard')
    }
  }, [output])

  const swap = useCallback(() => {
    setInput(output.slice(0, MAX_CODEC_INPUT_CHARS))
    setOutput('')
    setMode((m) => (m === 'encode' ? 'decode' : 'encode'))
    setError(null)
  }, [output])

  const inputColumn = (
    <LinedTextArea
      fillHeight
      value={input}
      onChange={(e) => setInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
      maxLength={MAX_CODEC_INPUT_CHARS}
      rows={14}
      wrapLines={lineWrap}
      placeholder={mode === 'encode' ? codec.placeholderEncode : codec.placeholderDecode}
      aria-label="JSON input"
    />
  )

  const toolSettings = <WrapLinesPanelSwitch lineWrap={lineWrap} onLineWrapChange={setLineWrap} />

  return (
    <>
      <Head title="JSON tools" />
      <ToolWorkspace
        heading="JSON prettify / minify"
        inputHeaderStart={<ToolIoChip>INPUT (RAW)</ToolIoChip>}
        toolSettings={toolSettings}
        inputHeaderEnd={
          <span className="font-mono text-[10px] text-on-surface-variant uppercase">
            {mode === 'encode' ? codec.hintEncode : codec.hintDecode}
          </span>
        }
        inputColumn={inputColumn}
        mode={mode}
        setMode={setMode}
        encodeOnly={false}
        swapDisabled={false}
        swapTitle="Swap I/O"
        onSwap={swap}
        onExecute={run}
        executeBusy={busy}
        output={output}
        onCopy={copy}
        error={error}
        statsCharValue={String(input.length).padStart(6, '0')}
        statsAlgorithm="JSON"
        statsOutputLen={String(output.length).padStart(6, '0')}
        statsModeLabel={mode === 'encode' ? 'PRETTY' : 'MINIFY'}
        outputLineNumbers
        lineWrap={lineWrap}
        about={JSON_ABOUT}
      />
    </>
  )
}
